import { Plus } from "lucide-react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { TriggerSourceType } from "@/shared/domain/triggers";
import {
  Field,
  FormSection,
  RobotoButton,
} from "@/shared/foundation/primitives";

import { defaultTargetKindFor } from "../formScope";
import { TargetCard } from "../targets/TargetCard";
import cards from "../targets/targets.module.css";
import {
  newTargetValues,
  type TargetDraftContext,
} from "../triggerFormDefaults";
import { type TriggerFormValues } from "../triggerFormSchema";

/**
 * What the trigger does when it fires. Targets are dispatched independently,
 * so the list keeps insertion order and offers no reordering.
 */
export function TargetsSection() {
  const { control, getFieldState, formState } =
    useFormContext<TriggerFormValues>();
  const firesOn = useWatch<TriggerFormValues, "firesOn">({
    control,
    name: "firesOn",
  });
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "targets",
  });

  const context: TargetDraftContext =
    firesOn.type === TriggerSourceType.Events
      ? { events: firesOn.events, firesOnSchedule: false }
      : { events: [], firesOnSchedule: true };
  const listError = getFieldState("targets", formState).error;

  return (
    <FormSection
      title="What it does"
      hint="Each target runs on its own, so the order carries no meaning."
    >
      {fields.length > 0 && (
        <div
          className={cards.list}
          data-testid="trigger-targets"
          data-count={fields.length}
        >
          {fields.map((field, index) => (
            <TargetCard
              key={field.id}
              index={index}
              onRemove={() => {
                remove(index);
              }}
              onChangeKind={(kind) => {
                update(index, newTargetValues(kind, context));
              }}
            />
          ))}
        </div>
      )}

      <RobotoButton
        variant="secondary"
        icon={<Plus aria-hidden size={16} />}
        onClick={() => {
          append(newTargetValues(defaultTargetKindFor(firesOn), context));
        }}
        data-testid="trigger-add-target"
      >
        Add target
      </RobotoButton>

      {listError?.message !== undefined && (
        <Field.Root invalid>
          <Field.Error match>{listError.message}</Field.Error>
        </Field.Root>
      )}
    </FormSection>
  );
}
